import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

interface Props {
  page: number;
  total: number;
  rowsPerPage: number;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage?: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  disable?: boolean;
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

type PageItem = number | 'start-ellipsis' | 'end-ellipsis';

// Ventana de páginas con ancho acotado (máx. 7 elementos) sin importar el
// total: 1 … actual-1, actual, actual+1 … última. Renderizar un botón por
// cada página ensanchaba el paginador (y el scroll de la página) sin límite.
const getPageItems = (page: number, lastPage: number): PageItem[] => {
  if (lastPage <= 7) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }
  const left = Math.max(page - 1, 2);
  const right = Math.min(page + 1, lastPage - 1);
  const items: PageItem[] = [1];
  if (left > 2) items.push('start-ellipsis');
  for (let current = left; current <= right; current++) items.push(current);
  if (right < lastPage - 1) items.push('end-ellipsis');
  items.push(lastPage);
  return items;
};

// Primer ancestro que actúa como scrollport del eje horizontal (el mismo que
// usa `position: sticky`); si no hay ninguno, el documento.
const getScrollParent = (element: HTMLElement): HTMLElement => {
  let parent = element.parentElement;
  while (parent && parent !== document.documentElement) {
    const { overflowX } = getComputedStyle(parent);
    if (overflowX !== 'visible' && overflowX !== 'clip') return parent;
    parent = parent.parentElement;
  }
  return document.documentElement;
};

const px = (value: string) => parseFloat(value) || 0;

interface StickyBox {
  left: number;
  width: number;
}

// Calcula dónde debe quedar el paginador para estar siempre a la vista:
// - left: su posición natural dentro del scrollport (así el sticky no salta).
// - width: del borde izquierdo natural hasta el borde derecho visible, menos
//   el margen real que dejan los ancestros (padding/borde/margen derecho). Si
//   la tabla no desborda, el contenedor es más angosto y manda su propio
//   ancho, o sea queda alineado exacto con las filas.
const measureStickyBox = (outer: HTMLElement): StickyBox => {
  const scroller = getScrollParent(outer);
  const isDocument = scroller === document.documentElement;
  const viewLeft = isDocument
    ? 0
    : scroller.getBoundingClientRect().left + scroller.clientLeft;
  const viewWidth = isDocument
    ? document.documentElement.clientWidth
    : scroller.clientWidth;
  const scrollLeft = isDocument ? window.scrollX : scroller.scrollLeft;
  const left = outer.getBoundingClientRect().left - viewLeft + scrollLeft;

  let gutter = 0;
  for (
    let element = outer.parentElement;
    element && element !== scroller;
    element = element.parentElement
  ) {
    const style = getComputedStyle(element);
    gutter +=
      px(style.paddingRight) +
      px(style.borderRightWidth) +
      px(style.marginRight);
  }

  return {
    left,
    width: Math.max(Math.min(outer.clientWidth, viewWidth - left - gutter), 0)
  };
};

const NewTablePagination = ({
  page,
  total,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  disable = false
}: Props) => {
  const lastPage = Math.max(1, Math.ceil(total / rowsPerPage));

  const startItem = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, total);

  const outerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<StickyBox | null>(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    const update = () => {
      const next = measureStickyBox(outer);
      setBox((current) =>
        current &&
        Math.abs(current.left - next.left) < 0.5 &&
        Math.abs(current.width - next.width) < 0.5
          ? current
          : next
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(outer);
    window.addEventListener('resize', update);
    // El sidebar cambia el layout con una transición CSS (sin cambiar el
    // tamaño de este nodo): al terminar, se vuelve a medir.
    window.addEventListener('transitionend', update, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('transitionend', update, true);
    };
  }, []);

  return (
    <div ref={outerRef} className="w-full">
      <div
        className="sticky flex items-center justify-between gap-4"
        style={box ? { left: box.left, width: box.width } : { width: '100%' }}
      >
        {onChangeRowsPerPage ? (
          <label className="flex items-center gap-2 text-sm text-inkMuted dark:text-inkMuted-dark">
            Filas por página
            <Autocomplete
              disabled={disable}
              size="small"
              options={rowsPerPageOptions}
              disableClearable={true}
              value={rowsPerPage}
              getOptionLabel={(option) => option.toString()}
              onChange={(_, value) => onChangeRowsPerPage(Number(value))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="rounded-lg border border-line bg-whiteLight px-2 py-1 text-sm text-ink outline-none transition focus:border-secondary-500 dark:border-white/10 dark:bg-darkLight dark:text-ink-dark"
                  variant="outlined"
                />
              )}
            />
          </label>
        ) : (
          <span />
        )}

        <div className="text-sm text-inkMuted dark:text-inkMuted-dark">
          {total === 0 ? (
            <span>Sin elementos</span>
          ) : (
            <span>
              {startItem}-{endItem} de {total}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            title="Primera página"
            disabled={page <= 1 || disable}
            onClick={() => onChangePage(1)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-inkMuted transition hover:bg-blueGray-100 disabled:opacity-40 dark:text-inkMuted-dark dark:hover:bg-white/10"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            type="button"
            title="Página anterior"
            disabled={page <= 1 || disable}
            onClick={() => onChangePage(page - 1)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-inkMuted transition hover:bg-blueGray-100 disabled:opacity-40 dark:text-inkMuted-dark dark:hover:bg-white/10"
          >
            <ChevronLeft size={16} />
          </button>
          {getPageItems(page, lastPage).map((item) => {
            if (typeof item !== 'number') {
              return (
                <span
                  key={item}
                  className="flex h-8 w-8 items-center justify-center text-sm text-inkMuted dark:text-inkMuted-dark"
                >
                  …
                </span>
              );
            }
            const pageNumber = item;
            return (
              <button
                key={pageNumber}
                type="button"
                disabled={disable}
                onClick={() => onChangePage(pageNumber)}
                className={
                  'h-8 w-8 rounded-full text-sm transition ' +
                  (pageNumber === page
                    ? 'bg-secondary-500 text-white'
                    : 'text-inkMuted hover:bg-blueGray-100 dark:text-inkMuted-dark dark:hover:bg-white/10')
                }
              >
                {pageNumber}
              </button>
            );
          })}
          <button
            type="button"
            title="Página siguiente"
            disabled={page >= lastPage || disable}
            onClick={() => onChangePage(page + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-inkMuted transition hover:bg-blueGray-100 disabled:opacity-40 dark:text-inkMuted-dark dark:hover:bg-white/10"
          >
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            title="Última página"
            disabled={page >= lastPage || disable}
            onClick={() => onChangePage(lastPage)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-inkMuted transition hover:bg-blueGray-100 disabled:opacity-40 dark:text-inkMuted-dark dark:hover:bg-white/10"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewTablePagination;
